// The live editor's view of a reference node (`trameriaLink`): the refs part's
// chip. The node keeps its kind, id and written name as they are (a kind this
// app does not know is written back unchanged); only the drawing differs.
//
// Pressing a chip: while the body is being written (focused), a plain click
// selects the chip (to delete it, to type around it) and Cmd / Ctrl + click
// opens it. While reading (not focused), a plain click opens it: the mousedown
// is stopped so neither the caret nor the focus moves (`chipMouseDownWhileReading`).
import type { NodeViewProps } from "@tiptap/core";
import { NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";
import { TrameriaLink, type TrameriaLinkAttrs } from "@trameria/editor";
import { RefChip } from "../../refs";

function RefNodeView({ node, editor }: NodeViewProps) {
  const attrs = node.attrs as TrameriaLinkAttrs;
  return (
    <NodeViewWrapper
      as="span"
      data-trameria-link=""
      data-trameria-link-kind={attrs.kind ?? ""}
      data-trameria-link-ref-id={attrs.refId ?? ""}
    >
      <RefChip
        kind={attrs.kind}
        id={attrs.refId}
        label={attrs.label}
        opensOn={(e) => !editor.view.hasFocus() || e.metaKey || e.ctrlKey}
      />
    </NodeViewWrapper>
  );
}

/** The reference node with the chip as its view (same name: it replaces the plain one). */
export const TrameriaLinkWithChip = TrameriaLink.extend({
  addNodeView() {
    return ReactNodeViewRenderer(RefNodeView);
  },
});

/** For `handleDOMEvents.mousedown`: a press on an openable chip while reading. */
export function chipMouseDownWhileReading(
  focused: boolean,
  event: MouseEvent,
): boolean {
  if (focused || event.button !== 0) return false;
  const target = event.target instanceof Element ? event.target : null;
  if (!target?.closest("a[data-ref]")) return false;
  event.preventDefault();
  return true;
}
